/**
 * Payment processing (piece 24; decision 0465): the two secrets the Stripe
 * adapter behind the `PaymentGatewayProvider` seam opens, held sealed in the
 * `KeyProvider` envelope beside the Maps and Vision keys. 🔨 A secret is
 * opened at the call and kept nowhere, so a key replaced in Settings is the
 * key the next charge uses.
 *
 * 🔨 No key is held in the repository, in an environment variable or in a
 * fixture. Nothing is bound to a rail until a `PaymentGatewayBinding` row
 * names the provider, so a build with no key charges nothing rather than
 * charging against somebody's live account.
 */

/** The secret key a charge and a refund are made with; a live key and a test key are the same row in two environments. */
export const STRIPE_SECRET_KEY_KEY = 'payments.stripeSecretKey';

/** The signing secret a delivered webhook is verified against; a different secret from the one above and never interchangeable with it. */
export const STRIPE_WEBHOOK_SECRET_KEY = 'payments.stripeWebhookSecret';

/**
 * The key the BROWSER identifies this Stripe account with, so the bank
 * sign-in screen can be opened from the page (decision 0492).
 *
 * 🔨 It is NOT sealed, and that is deliberate. A publishable key is public by
 * design — it is shipped to every browser that loads the screen — and putting
 * it in the envelope beside the secret key would tell a reader the two are
 * the same kind of thing. They are not: one opens a session, the other moves
 * money.
 */
export const STRIPE_PUBLISHABLE_KEY_KEY = 'payments.stripePublishableKey';

/** Our own key for the adapter, the value a `PaymentGatewayBinding` row carries in `providerKey`. */
export const STRIPE_GATEWAY_KEY = 'stripe';

/**
 * 🔒 Where Stripe delivers its events (decision 1649): open without a session,
 * and the signature over the raw body is the route's whole gate. The page that
 * guides the setup shows this path under the API's own address.
 */
export const STRIPE_WEBHOOK_ROUTE = '/payments/stripe/webhook';

/** 🔒 Each client address is counted on the webhook per hour (decision 1505's rule for an open route); Stripe retries a refused delivery. */
export const STRIPE_WEBHOOKS_PER_ADDRESS_PER_HOUR = 600;

/**
 * 🔒 The keys a payment the Checkout opened carries back on its event
 * (decisions 1649, 1650): the invoice it pays and the surcharge it added. A
 * payment carrying no invoice key is not ours, and its event is acknowledged
 * and ignored.
 */
export const PAYMENT_REFERENCE_INVOICE = 'bms2InvoiceId';
export const PAYMENT_REFERENCE_SURCHARGE = 'bms2SurchargeCents';
